import type { Styles } from '@adornis/ass/style.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { UserRole } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import {
  ReportedErrorDataviewLine,
  subscribeAllSlotsWithReportedErrors,
} from '../../../db/dataviews/ReportedErrorsDataviewLine.js';
import { ILSUser } from '../../../db/ils-user.js';
import { ILSEvaluationInspectDialog } from '../../components/ils-evaluation-inspect.js';

const headerCell = (text: string) => html` <x-flex padding="xs" center> <x-text bold> ${text} </x-text></x-flex> `;

@customElement('ils-evaluation-reported-errors')
export class ILSEvaluationReportedErrors extends ChemistryLitElement {
  @state() private readonly _inspectors = new RXController(
    this,
    ILSUser.getUsersByRoles([UserRole.INSPECTOR])({
      _id: 1,
      _class: 1,
      username: 1,
      specialization: 1,
      ecommerce: { _class: 1, firstName: 1, lastName: 1 },
    }),
  );

  override render() {
    if (this._inspectors.isLoading)
      return html`
        <x-flex space="lg" w100 h100 center crossaxis-center>
          <ils-loader> </ils-loader>
        </x-flex>
      `;
    return html`
      <x-data-view
        .options=${{
          class: ReportedErrorDataviewLine,
          operation: subscribeAllSlotsWithReportedErrors,
          loader: () =>
            html` <x-flex ${css({ minHeight: '200px' })} center crossaxis-center>
              <ils-loader></ils-loader>
            </x-flex>`,
          isEntryClickable: true,
          customColumns: [
            {
              fieldName: 'slot.osce.osceType',
              renderItem: entityInstance => html` <ils-osce-type .osce=${entityInstance.slot.osce}></ils-osce-type> `,
              renderHeader: () => nothing,
              columnWidth: 'max-content',
              selectionSet: { slot: { osce: { osceType: 1, taskType: 1, enableVideo: 1, conversationType: 1 } } },
            },
            { fieldName: 'slot.osce.titleTrainee', renderHeader: () => headerCell('OSCE') },
            {
              fieldName: 'user.ecommerce.firstName',
              renderItem: entityInstance => html` <x-text> ${entityInstance.user.ecommerce?.fullName} </x-text> `,
              renderHeader: () => headerCell('Nutzer'),
              selectionSet: { user: { ecommerce: { firstName: 1, lastName: 1 } } },
            },
            {
              fieldName: 'slot.startTime',
              renderItem: entityInstance =>
                html` <x-text> ${entityInstance.slot.startTime.toLocaleString(DateTime.DATE_SHORT)} </x-text> `,
              renderHeader: () => headerCell('Datum'),
            },
          ],
        } satisfies DataViewOptions<typeof ReportedErrorDataviewLine>}
        @entry-clicked=${(
          event: CustomEvent<{
            column: ColumnDefinition<typeof ReportedErrorDataviewLine>;
            entity: ReportedErrorDataviewLine;
          }>,
        ) => {
          return ILSEvaluationInspectDialog.showPopup({
            props: { slotID: event.detail.entity.slot._id, inspectors: this._inspectors.value },
          });
        }}
      ></x-data-view>
    `;
  }

  override styles(): Styles[] {
    return [...super.styles(), { ':host': { flex: '1', display: 'flex', flexDirection: 'column' } }];
  }
}
